/**
 * Wholesale price helpers — supports DB format (min_qty + discount)
 * and legacy format (minQuantity + fixed price).
 */

export function getWholesalePricing(product) {
  if (!product) return null;
  const raw = product.wholesale_pricing ?? product.wholesalePricing;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

export function getRetailPriceCents(product) {
  if (!product) return 0;
  const cents = product.price_cents ?? product.price ?? 0;
  return Math.max(0, Math.round(Number(cents) || 0));
}

export function getWholesalePriceCents(product, tier = 'standard', quantity = 1, options = {}) {
  const { ignoreMinQty = false } = options;
  const retail = getRetailPriceCents(product);
  const wp = getWholesalePricing(product);
  if (!wp || !wp[tier]) return retail;

  const tierData = wp[tier];
  const minQty = Number(tierData.minQuantity ?? tierData.min_qty ?? 1);
  const qty = Math.max(1, Number(quantity) || 1);
  if (!ignoreMinQty && qty < minQty) return retail;

  if (tierData.price != null && Number.isFinite(Number(tierData.price))) {
    return Math.max(0, Math.round(Number(tierData.price)));
  }
  if (tierData.discount != null && Number.isFinite(Number(tierData.discount))) {
    const discount = Math.min(100, Math.max(0, Number(tierData.discount)));
    return Math.max(0, Math.round(retail * (1 - discount / 100)));
  }
  return retail;
}

export function resolveLineUnitPriceCents(product, {
  pricingMode = 'retail',
  wholesaleTier = 'standard',
  quantity = 1,
  unitPriceCents = null,
  ignoreMinQty = false,
} = {}) {
  if (pricingMode === 'custom' && unitPriceCents != null) {
    return Math.max(0, Math.round(Number(unitPriceCents) || 0));
  }
  if (pricingMode === 'wholesale') {
    return getWholesalePriceCents(product, wholesaleTier, quantity, { ignoreMinQty });
  }
  return getRetailPriceCents(product);
}

/** Mail/check orders apply product wholesale tier discounts even below shop min qty. */
export const MAIL_ORDER_WHOLESALE_OPTIONS = { ignoreMinQty: true };

export function getWholesaleMinQty(product, tier = 'standard') {
  const wp = getWholesalePricing(product);
  if (!wp?.[tier]) return null;
  const tierData = wp[tier];
  return Number(tierData.minQuantity ?? tierData.min_qty ?? null) || null;
}

/** Catalog wholesale tier metadata for mail-order UI. */
export function getWholesaleTierMeta(product, tier = 'standard', quantity = 1, options = {}) {
  const retail = getRetailPriceCents(product);
  const wp = getWholesalePricing(product);
  if (!wp?.[tier]) {
    return {
      retail,
      wholesalePrice: retail,
      catalogDiscountPct: 0,
      minQty: null,
      hasTier: false,
      fixedPrice: false,
    };
  }

  const tierData = wp[tier];
  const minQty = Number(tierData.minQuantity ?? tierData.min_qty ?? 1);
  const wholesalePrice = getWholesalePriceCents(product, tier, quantity, options);
  const fixedPrice = tierData.price != null && Number.isFinite(Number(tierData.price));

  let catalogDiscountPct = 0;
  if (tierData.discount != null && Number.isFinite(Number(tierData.discount))) {
    catalogDiscountPct = Math.min(100, Math.max(0, Number(tierData.discount)));
  } else if (retail > 0 && wholesalePrice < retail) {
    catalogDiscountPct = Math.round((1 - wholesalePrice / retail) * 100);
  }

  return {
    retail,
    wholesalePrice,
    catalogDiscountPct,
    minQty,
    hasTier: true,
    fixedPrice,
  };
}

export function applyExtraDiscountCents(priceCents, extraDiscountPct) {
  const discount = Math.min(100, Math.max(0, Number(extraDiscountPct) || 0));
  return Math.max(0, Math.round(Number(priceCents || 0) * (1 - discount / 100)));
}

export function getEffectiveDiscountPct(retailCents, appliedCents) {
  const retail = Math.max(0, Math.round(Number(retailCents) || 0));
  const applied = Math.max(0, Math.round(Number(appliedCents) || 0));
  if (retail <= 0 || applied >= retail) return 0;
  return Math.round((1 - applied / retail) * 100);
}

export function productHasWholesaleTier(product, tier = 'standard') {
  const wp = getWholesalePricing(product);
  return Boolean(wp?.[tier]);
}

export const WHOLESALE_TIERS = [
  { id: 'standard', label: 'Standard wholesale' },
  { id: 'premium', label: 'Premium wholesale' },
  { id: 'volume', label: 'Volume wholesale' },
];
